import { Box, Typography, Divider } from '@mui/material';
import { OATypography } from '../../../../components/widgets';
import { ReactComponent as SuccessIcon } from '../../../../assets/icons/success.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/error.svg';
import { calculateNights } from '../../../../helper/timeDifference';
import OrderInvoice from '../../OrderInvoice';
import { getRefundTable } from '../PaymentSummaryTables/customerTables';
import { IHotelData } from '../../type';

interface ICustomerProps {
  data: IHotelData;
  isLoading: boolean;
}
const PaymentLineItem = ({ label, amount, isTotal = false, isNegative = false }: { 
  label: string; 
  amount: number | string; 
  isTotal?: boolean;
  isNegative?: boolean;
}) => (
  <>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 8px'
      }}>
      <Typography variant={isTotal ? 'subtitle2' : 'body2'}>
        {label}
      </Typography>
      <Typography 
        variant={isTotal ? 'subtitle2' : 'body2'}
        sx={isNegative ? { color: 'success.main' } : undefined}
      >
        {isNegative ? '- ' : ''}INR {amount}
      </Typography>
    </Box>
    <Divider />
  </>
);

const CoinsEarnedBanner = ({ coinsEarned }: { coinsEarned: number }) => (
  <Box
    display='flex'
    height='40px'
    borderRadius='10px'
    alignItems='center'
    justifyContent='start'
    margin='16px 16px 0px 16px'
    sx={{ backgroundColor: 'success.light' }}>
    <SuccessIcon style={{ marginLeft: '10px', color: 'success.main' }} />
    <Box display='flex' alignItems='center'>
      <OATypography variant='subtitle2' sx={{ fontSize: '12px', marginLeft: '10px' }}>
        {coinsEarned}
      </OATypography>
      <OATypography variant='body2' sx={{ fontSize: '12px', lineHeight: '16px', paddingLeft: '4px' }}>
        Niyo Global Coins earned on this trip.
      </OATypography>
    </Box>
  </Box>
);

export default function Customer({ data, isLoading }: ICustomerProps) {
  const orgCode = data?.userOrganization?.organizationCode;
  const paymentSummary = data?.orderAmount?.paymentSummary;
  const refundTableData = paymentSummary?.refundSummary && getRefundTable({ data, isLoading });
  
  const totalRooms = data?.roomCount ?? data?.rooms?.length;
  const totalNights = calculateNights(data?.checkInDate, data?.checkOutDate);
  
  const strikeOfTotalAmountBreakup = data?.orderAmount?.strikeOfTotalAmountBreakup ?? {};
  const baseFare = strikeOfTotalAmountBreakup?.baseAmount ?? data?.orderAmount?.baseAmount ?? 0;
  const taxesandfees = strikeOfTotalAmountBreakup?.taxAmount ?? data?.orderAmount?.totalTaxAndFee ?? data?.orderAmount?.taxAmount ?? 0;
  const subTotal = strikeOfTotalAmountBreakup?.totalAmount ?? data?.orderAmount?.strikeOfTotalAmount ?? 0;
  
  const amountInCoins = paymentSummary?.amountInCoins ?? 0;
  const convenienceFee = data?.orderAmount?.convenienceFee ?? 0;
  const paymentMode = data?.orderAmount?.paymentSummary?.paymentMode ?? 'UPI';
  const totalAmount = data?.orderAmount?.paymentSummary?.amountInCash ?? 0;
  
  const discountDisplay = data?.orderAmount?.discountTotalAmount?.discountBreakUps?.[0]?.display ?? "N/A";
  const discountAmount = data?.orderAmount?.discountTotalAmount?.totalDiscountAmount ?? 0;
  const coinsEarned = data?.orderAmount?.paymentSummary?.coinsEarned ?? 0;

  const addOnServicesObj = data?.orderAmount?.addOnServicesAmount
  return (
    <Box>
      {coinsEarned > 0 &&
        (!orgCode || orgCode?.toLowerCase()?.includes('niyo')) && (
          <CoinsEarnedBanner coinsEarned={coinsEarned} />
        )}

      <div className='card-body py-0'>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 8px'
          }}>
          <Typography variant='subtitle2' sx={{ fontSize: '12px', lineHeight: '16px', color: 'grey.400' }}>
            PRODUCT
          </Typography>
          <Typography variant='subtitle2' sx={{ fontSize: '12px', lineHeight: '16px', color: 'grey.400' }}>
            AMOUNT
          </Typography>
        </Box>
        <Divider />

        <PaymentLineItem label={`${totalRooms} room x ${totalNights} nights`} amount={baseFare} />
        <PaymentLineItem label="Taxes and fees" amount={taxesandfees} />
        <PaymentLineItem label="Sub Total" amount={subTotal ?? 'N/A'} isTotal />
        <PaymentLineItem label="Convenience Fee" amount={convenienceFee} />
        
        {amountInCoins > 0 && (
          <PaymentLineItem label="Niyo global coins discount" amount={amountInCoins} isNegative />
        )}
        
        {discountAmount > 0 && (
          <PaymentLineItem label={discountDisplay} amount={discountAmount} isNegative />
        )}

        {addOnServicesObj && addOnServicesObj?.addonServiceBreakUps && addOnServicesObj?.totalAmount > 0 && (
          addOnServicesObj?.addonServiceBreakUps?.map((item: any) => (
            <PaymentLineItem label={item?.type?.replace(/_/g, ' ')} amount={item?.totalAmount ?? ''} />
          ))
        )}
        
        <PaymentLineItem 
          label={`Total(Amount Paid via ${paymentMode})`} 
          amount={totalAmount ?? 'N/A'} 
          isTotal 
        />
      </div>

      {paymentSummary?.refundSummary && (
        <Box sx={{ padding: '16px' }}>
          <OATypography variant='subtitle2'>Refund details</OATypography>
          {(!orgCode || orgCode?.toLowerCase()?.includes('niyo')) && (
            <Box
              display='flex'
              height='40px'
              borderRadius='10px'
              alignItems='center'
              justifyContent='start'
              margin='16px 0px 0px 0px'
              sx={{
                backgroundColor: paymentSummary?.refundSummary?.refundCoin > 0
                  ? 'success.light'
                  : '#FFE5E2'
              }}>
              {paymentSummary?.refundSummary?.refundCoin > 0 ? (
                <>
                  <SuccessIcon style={{ marginLeft: '10px' }} />
                  <Box display='flex' alignItems='center'>
                    <OATypography variant='subtitle2' sx={{ fontSize: '12px', marginLeft: '10px' }}>
                      {paymentSummary?.coinsEarned}
                    </OATypography>
                    <OATypography variant='body2' sx={{ fontSize: '12px', lineHeight: '16px', paddingLeft: '4px' }}>
                      Niyo Global Coins will be added back.
                    </OATypography>
                  </Box>
                </>
              ) : (
                <>
                  <ErrorIcon style={{ marginLeft: '10px' }} />
                  <OATypography variant='body2' sx={{ fontSize: '12px', lineHeight: '16px', marginLeft: '10px' }}>
                    No coins are being refunded for this trip
                  </OATypography>
                </>
              )}
            </Box>
          )}
          <OrderInvoice tableData={refundTableData} loading={isLoading} />
        </Box>
      )}
    </Box>
  );
}
